import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
  Tooltip,
  Typography,
  Box,
  Avatar,
  Badge,
  Chip,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import InventoryIcon from "@mui/icons-material/Inventory";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupIcon from "@mui/icons-material/Group";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Insights from "@mui/icons-material/Insights";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutIcon from "@mui/icons-material/Logout";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ShopIcon from '@mui/icons-material/Shop';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import { useAuth } from "../auth-context";
import LOGO from '../assets/abLogo.svg';

const Sidebar = ({ onCollapse }) => {
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { userRole, logout, user } = useAuth();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;

  // **Role-based menu items**
  const getMenuItems = () => {
    const allMenuItems = [
      {
        path: "/dashboard",
        icon: DashboardIcon,
        text: "Dashboard",
        color: "#1A237E",
        badge: null,
        description: "Overview & Analytics",
        roles: ["superadmin", "admin", "chef"] // All roles can access dashboard
      },
      {
        path: "/itemTracking",
        icon: Insights,
        text: "Item Tracking",
        color: "#1A237E",
        badge: null,
        description: "Item & Analytics",
        roles: ["superadmin"] // Only superadmin
      },
      {
        path: "/inventory",
        icon: InventoryIcon,
        text: "Inventory",
        color: "#2E7D32",
        badge: null,
        description: "Stock Management",
        roles: ["superadmin", "admin"] // superadmin + admin
      },
      {
        path: "/MandatoryItemsScreen",
        icon: TipsAndUpdatesIcon,
        text: "Mandatory Items",
        color: "#2E7D32",
        badge: null,
        description: "Mobile Order",
        roles: ["superadmin", "admin"] // superadmin + admin
      },
      {
        path: "/cutoff-notifications",
        icon: NotificationAddIcon,
        text: "Schedule Notifications",
        color: "#2E7D32",
        badge: null,
        description: "Mobile notifications",
        roles: ["superadmin", "admin"] // superadmin + admin
      },
      {
        path: "/invoices",
        icon: ShoppingCartIcon,
        text: "Orders",
        color: "#FF6F00",
        badge: null,
        description: "Active Orders",
        roles: ["superadmin"] // superadmin + admin
      },
      {
        path: "/ConsolidatedInvoiceScreen",
        icon: ReceiptIcon,
        text: "Invoices",
        color: "#00BCD4",
        badge: null,
        description: "Billing & Payments",
        roles: ["superadmin", "admin"] // superadmin + admin
      },
      {
        path: "/purchaseInventory",
        icon: ShoppingBasketIcon,
        text: "Purchase Inventory",
        color: "#F57C00",
        badge: null,
        description: "Procurement",
        roles: ["superadmin", "admin"] // superadmin + admin
      },
      {
        path: "/purchaseOrder",
        icon: ShopIcon,
        text: "Purchase Order",
        color: "#1A237E",
        badge: null,
        description: "Order Management",
        roles: ["superadmin", "admin"] // superadmin + admin
      },
      {
        path: "/ingredients",
        icon: RestaurantIcon,
        text: "Ingredients",
        color: "#2E7D32",
        badge: null,
        description: "Recipe Components",
        roles: ["superadmin"] // Only superadmin
      },
      {
        path: "/waste-management",
        icon: DeleteIcon,
        text: "Waste Management",
        color: "#C62828",
        badge: null,
        description: "Sustainability",
        roles: ["superadmin", "admin"] // superadmin + admin
      },
      // Superadmin only items
      ...(userRole === "superadmin" ? [
        {
          path: "/add-users",
          icon: PersonAddIcon,
          text: "Add Users",
          color: "#00BCD4",
          badge: null,
          description: "User Registration",
          roles: ["superadmin"]
        },
        {
          path: "/manage-users",
          icon: GroupIcon,
          text: "Manage Users",
          color: "#F57C00",
          badge: null,
          description: "User Administration",
          roles: ["superadmin"]
        },
      ] : []),
    ];

    // Filter menu items based on user role
    return allMenuItems.filter(item => item.roles.includes(userRole));
  };

  const menuItems = getMenuItems();

  // **Theme and styles remain the same as your original code**
  const crispyDosaTheme = {
    colors: {
      primary: "#1A237E",
      secondary: "#FF6F00",
      accent: "#00BCD4",
      success: "#2E7D32",
      warning: "#F57C00",
      error: "#C62828",
      surface: "#FAFAFA",
      background: "#FFFFFF",
      onSurface: "#212121",
      onPrimary: "#FFFFFF",
      neutral: {
        50: "#FAFAFA",
        100: "#F5F5F5",
        200: "#EEEEEE",
        300: "#E0E0E0",
        400: "#BDBDBD",
        500: "#9E9E9E",
        600: "#757575",
        700: "#616161",
        800: "#424242",
        900: "#212121"
      }
    },
    shadows: {
      card: "0 4px 20px rgba(26, 35, 126, 0.08)",
      elevated: "0 8px 32px rgba(26, 35, 126, 0.12)",
      floating: "0 16px 48px rgba(26, 35, 126, 0.15)"
    },
    gradients: {
      primary: "linear-gradient(135deg, #1A237E 0%, #3949AB 50%, #5C6BC0 100%)",
      secondary: "linear-gradient(135deg, #FF6F00 0%, #FF8F00 50%, #FFA000 100%)",
      accent: "linear-gradient(135deg, #00BCD4 0%, #26C6DA 50%, #4DD0E1 100%)"
    }
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const toggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    onCollapse(newCollapsed);
  };
  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const drawerWidth = () => {
    if (isMobile) return "300px";
    return collapsed ? "80px" : "280px";
  };

  // **Styles remain the same as your original code**
  const styles = {
    drawer: {
      width: drawerWidth(),
      flexShrink: 0,
      "& .MuiDrawer-paper": {
        width: drawerWidth(),
        boxSizing: "border-box",
        background: `linear-gradient(180deg, ${crispyDosaTheme.colors.surface} 0%, ${crispyDosaTheme.colors.background} 100%)`,
        borderRight: `2px solid ${crispyDosaTheme.colors.neutral[100]}`,
        boxShadow: crispyDosaTheme.shadows.elevated,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflowX: "hidden",
      },
    },
    brandHeader: {
      background: crispyDosaTheme.gradients.secondary,
      padding: collapsed ? "6px 20px" : "10px 10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      minHeight: "100px",
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: crispyDosaTheme.gradients.accent,
      },
    },
    logoSection: {
      display: "flex",
      // alignItems: "center",
      opacity: collapsed ? 0 : 1,
      transition: "opacity 0.3s ease",
      overflow: "hidden",
    },
    clientLogo: {
      width: 48,
      height: 48,
      borderRadius: "12px",
      marginRight: collapsed ? 0 : "16px",
      border: `2px solid ${crispyDosaTheme.colors.onPrimary}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
    brandInfo: {
      color: crispyDosaTheme.colors.onPrimary,
      display: collapsed ? "none" : "block",
    },
    brandTitle: {
      fontSize: "1.3rem",
      fontWeight: 800,
      lineHeight: 1.2,
      marginBottom: "4px",
      textShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    brandSubtitle: {
      fontSize: "0.8rem",
      opacity: 0.9,
      fontWeight: 500,
      letterSpacing: "0.5px",
    },
    controlButton: {
      color: crispyDosaTheme.colors.onPrimary,
      backgroundColor: "rgba(255, 255, 255, 0.15)",
      backdropFilter: "blur(10px)",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.25)",
        transform: "scale(1.05)",
      },
    },
    userProfile: {
      padding: collapsed ? "16px 12px" : "20px",
      backgroundColor: crispyDosaTheme.colors.background,
      display: "flex",
      alignItems: "center",
      gap: "12px",
      borderBottom: `1px solid ${crispyDosaTheme.colors.neutral[200]}`,
      margin: "0 8px",
      borderRadius: "16px",
      marginTop: "8px",
      boxShadow: crispyDosaTheme.shadows.card,
    },
    userAvatar: {
      width: 44,
      height: 44,
      background: crispyDosaTheme.gradients.accent,
      fontSize: "1.1rem",
      fontWeight: 700,
      border: `2px solid ${crispyDosaTheme.colors.background}`,
      boxShadow: crispyDosaTheme.shadows.card,
    },
    userDetails: {
      flex: 1,
      opacity: collapsed ? 0 : 1,
      transition: "opacity 0.3s ease",
      overflow: "hidden",
    },
    userName: {
      fontSize: "1rem",
      fontWeight: 700,
      color: crispyDosaTheme.colors.onSurface,
      marginBottom: "2px",
    },
    userStatus: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    roleChip: {
      height: "20px",
      fontSize: "0.7rem",
      fontWeight: 600,
      backgroundColor: crispyDosaTheme.colors.primary,
      color: crispyDosaTheme.colors.onPrimary,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    menuContainer: {
      flex: 1,
      padding: "16px 8px",
      overflowY: "auto",
    },
    menuGroup: {
      marginBottom: "8px",
    },
    menuItemContainer: (isItemActive, itemColor) => ({
      margin: "4px 0",
      borderRadius: "16px",
      overflow: "hidden",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 8px 25px ${itemColor}25`,
      },
    }),
    menuItemButton: (isItemActive, itemColor) => ({
      minHeight: "56px",
      borderRadius: "16px",
      padding: collapsed ? "12px" : "12px 16px",
      backgroundColor: isItemActive
        ? `${itemColor}15`
        : crispyDosaTheme.colors.background,
      border: isItemActive
        ? `2px solid ${itemColor}`
        : `2px solid transparent`,
      "&:hover": {
        backgroundColor: `${itemColor}10`,
        borderColor: `${itemColor}50`,
      },
    }),
    menuIcon: (isItemActive, itemColor) => ({
      minWidth: 0,
      marginRight: collapsed ? 0 : "16px",
      justifyContent: "center",
      "& .MuiSvgIcon-root": {
        fontSize: "1.4rem",
        color: isItemActive ? itemColor : crispyDosaTheme.colors.neutral[600],
        transition: "all 0.2s ease",
      },
    }),
    menuTextContent: {
      opacity: collapsed ? 0 : 1,
      transition: "opacity 0.2s ease",
      flex: 1,
    },
    menuText: (isItemActive, itemColor) => ({
      "& .MuiTypography-root": {
        fontSize: "0.9rem",
        fontWeight: isItemActive ? 700 : 600,
        color: isItemActive ? itemColor : crispyDosaTheme.colors.onSurface,
        marginBottom: "2px",
      },
    }),
    menuDescription: {
      fontSize: "0.75rem",
      color: crispyDosaTheme.colors.neutral[500],
      fontWeight: 500,
      display: collapsed ? "none" : "block",
    },
    menuBadge: {
      "& .MuiBadge-badge": {
        backgroundColor: crispyDosaTheme.colors.error,
        color: crispyDosaTheme.colors.onPrimary,
        fontSize: "0.7rem",
        fontWeight: 600,
        minWidth: "18px",
        height: "18px",
      },
    },
    footer: {
      padding: "16px 12px 20px",
      borderTop: `1px solid ${crispyDosaTheme.colors.neutral[200]}`,
    },
    logoutButton: {
      width: "100%",
      minHeight: "52px",
      borderRadius: "16px",
      background: crispyDosaTheme.gradients.secondary,
      color: crispyDosaTheme.colors.onPrimary,
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: collapsed ? "center" : "flex-start",
      padding: collapsed ? "0" : "0 20px",
      fontSize: "0.95rem",
      fontWeight: 700,
      transition: "all 0.3s ease",
      boxShadow: crispyDosaTheme.shadows.card,
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: crispyDosaTheme.shadows.elevated,
      },
    },
    logoutText: {
      marginLeft: collapsed ? 0 : "12px",
      display: collapsed ? "none" : "block",
    },
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Enhanced Brand Header */}
      <Box sx={styles.brandHeader}>
        <Box sx={styles.logoSection}>
          <Box sx={styles.brandInfo}>
            <img
              src={LOGO}
              alt="Client Logo"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
        </Box>
        <IconButton
          onClick={isMobile ? handleDrawerToggle : toggleCollapse}
          sx={styles.controlButton}
          size="small"
        >
          {isMobile ? <CloseIcon /> : collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      {/* Enhanced User Profile */}
      <Box sx={styles.userProfile}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: crispyDosaTheme.colors.success,
                border: `2px solid ${crispyDosaTheme.colors.background}`,
              }}
            />
          }
        >
          <Avatar sx={styles.userAvatar}>
            {user?.name?.charAt(0) || 'U'}
          </Avatar>
        </Badge>
        <Box sx={styles.userDetails}>
          <Typography sx={styles.userName}>
            {user?.name || 'User'}
          </Typography>
          <Box sx={styles.userStatus}>
            <Chip
              label={userRole === 'chef' ? 'Chef' : userRole}
              size="small"
              sx={styles.roleChip}
            />
          </Box>
        </Box>
        {!collapsed && (
          <IconButton size="small">
            <NotificationsIcon sx={{ color: crispyDosaTheme.colors.neutral[600] }} />
          </IconButton>
        )}
      </Box>

      {/* Enhanced Menu */}
      <Box sx={styles.menuContainer}>
        <List>
          {menuItems.map(({ path, icon: Icon, text, color, badge, description }) => (
            <Box key={path} sx={styles.menuItemContainer(isActive(path), color)}>
              <Tooltip
                title={collapsed ? `${text} - ${description}` : ""}
                placement="right"
                arrow
              >
                <ListItem
                  button
                  component={Link}
                  to={path}
                  sx={styles.menuItemButton(isActive(path), color)}
                  onClick={isMobile ? handleDrawerToggle : undefined}
                >
                  <ListItemIcon sx={styles.menuIcon(isActive(path), color)}>
                    <Badge
                      badgeContent={badge}
                      sx={styles.menuBadge}
                      invisible={!badge}
                    >
                      <Icon />
                    </Badge>
                  </ListItemIcon>
                  <Box sx={styles.menuTextContent}>
                    <ListItemText
                      primary={text}
                      sx={styles.menuText(isActive(path), color)}
                    />
                    <Typography sx={styles.menuDescription}>
                      {description}
                    </Typography>
                  </Box>
                </ListItem>
              </Tooltip>
            </Box>
          ))}
        </List>
      </Box>

      {/* Enhanced Footer */}
      <Box sx={styles.footer}>
        <button onClick={handleLogout} style={styles.logoutButton}>
          <LogoutIcon />
          <span style={styles.logoutText}>Sign Out</span>
        </button>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile && !mobileOpen && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: "fixed",
            left: "20px",
            top: "20px",
            zIndex: 1300,
            background: crispyDosaTheme.gradients.primary,
            color: crispyDosaTheme.colors.onPrimary,
            boxShadow: crispyDosaTheme.shadows.floating,
            width: 56,
            height: 56,
            "&:hover": {
              transform: "scale(1.1) rotate(90deg)",
              boxShadow: crispyDosaTheme.shadows.elevated,
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        sx={styles.drawer}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;